<script setup>
import {computed, getCurrentInstance, onMounted, ref} from 'vue'
import store from '@/store'
import helpers from '@/lib/helpers'
import NotificationRuleEditor from './notification-rule-editor.vue'
import NotificationRuleActivity from './notification-rule-activity.vue'

const {proxy} = getCurrentInstance()

const GROUP_ICON = {
  Orders: 'clipboard-list',
  Bids: 'gavel',
  'Purchase Orders': 'truck',
  Offers: 'handshake',
  Messages: 'envelope',
  Transactions: 'dollar-sign',
  Notes: 'note-sticky',
  System: 'gear',
  Shipping: 'box',
  Inventory: 'warehouse',
  Listings: 'store',
  Tags: 'tag',
  Files: 'paperclip',
  Vendors: 'store',
  Locations: 'map-pin',
  Customers: 'user',
  Users: 'user-gear',
}
const groupIcon = (g) => GROUP_ICON[g] || 'bell'

// A trigger that spans several unrelated record types (tag/note/file added)
// gets folded into whichever group each scope already has its own dedicated
// events under - e.g. "Tag added — Auction" shows under "Listings" alongside
// the other listing events, instead of a separate generic "Tags" heading.
const SCOPE_GROUP = {
  Order: 'Orders', Bid: 'Bids', BidLine: 'Bids', Auction: 'Listings', Item: 'Inventory',
  Vendor: 'Vendors', PO: 'Purchase Orders', Customer: 'Customers', Transaction: 'Transactions',
  Location: 'Locations', Warehouse: 'Inventory', Message: 'Messages', User: 'Users',
}

// re-groups the server's group -> [trigger] catalogue so multi-scope triggers
// appear once per scope, under that scope's home group (falls back to the
// trigger's own group for any scope not in SCOPE_GROUP, so nothing is lost)
const displayGroups = computed(() => {
  const raw = (catalog.value && catalog.value.groups) || {}
  const out = {}
  const push = (group, entry) => {
    (out[group] || (out[group] = [])).push(entry)
  }

  for (const [group, triggers] of Object.entries(raw)) {
    for (const t of triggers) {
      const scopes = t.scopes || []
      if (scopes.length > 1) {
        for (const scope of scopes) {
          push(SCOPE_GROUP[scope] || group, {...t, scope, label: `${t.label} — ${scope}`})
        }
      } else {
        push(group, t)
      }
    }
  }
  return out
})

// admin: manage another user's rules
const canAny = helpers.can('notifications.rules.any.read')
const selfId = store.state.auth.id
const targetUser = ref(selfId)
const userOptions = computed(() => {
  const list = (store.state.settings.defaults.users || []).map(u => ({value: u.id, text: u.name}))
  return [{value: selfId, text: 'My notifications'}, ...list.filter(u => u.value !== selfId)]
})
const basePath = computed(() =>
  `/api/user/${!canAny || targetUser.value === selfId ? 'self' : targetUser.value}/notification/rule`)

const catalog = ref(null)          // {groups, channels, operators}
const rules = ref([])
const loadError = ref('')

// view state: 'browse' (pick a trigger) | 'edit' | 'activity'
const mode = ref('browse')
const draft = ref(null)            // rule object being edited (new or existing)
const activityRule = ref(null)

const loadingRules = computed(() => store.getters.activity_alt('nrules'))

async function loadCatalog() {
  if (catalog.value) return
  try {
    catalog.value = await helpers.asyncAction(`${basePath.value}/trigger`, 'GET', {noload: 'nrules', quiet: true})
  } catch (e) {
    loadError.value = e.message || 'Could not load the notification catalogue'
  }
}

async function loadRules() {
  try {
    const r = await helpers.asyncAction(basePath.value, 'GET', {noload: 'nrules', quiet: true})
    rules.value = Array.isArray(r) ? r : (r.data || [])
    loadError.value = ''
  } catch (e) {
    loadError.value = e.message || 'Could not load rules'
  }
}

async function switchUser() {
  catalog.value = null
  mode.value = 'browse'
  draft.value = null
  await Promise.all([loadCatalog(), loadRules()])
}

function startNew(trigger, scopeType) {
  draft.value = {
    id: null,
    action: trigger.key,
    trigger,
    name: '',
    condition: '_default',
    channels: [...(catalog.value.channels || ['inapp', 'push'])],
    enabled: true,
    // type-only scope ("any Order"), not one specific record - the editor's
    // Scope row lets the user narrow to a GLI or remove it from here
    about: scopeType ? {type: scopeType, id: null, gli: null} : null,
  }
  mode.value = 'edit'
}

async function editRule(rule) {
  // fetch full rule so we get trigger meta (vars/fields) for the builder
  try {
    const full = await helpers.asyncAction(rule._links.self_url.href, 'GET', {noload: 'nrules', quiet: true})
    draft.value = {...full, trigger: full.trigger}
    mode.value = 'edit'
  } catch (e) {
    helpers.alert(e.message || 'Could not open rule')
  }
}

function openActivity(rule) {
  activityRule.value = rule
  mode.value = 'activity'
}

async function toggleEnabled(rule) {
  try {
    await helpers.asyncAction(rule._links.update_url.href, 'PUT', {
      noload: 'nrules', quiet: true,
      data: {action: rule.action, enabled: !rule.enabled},
    })
    rule.enabled = !rule.enabled
  } catch (e) {
    helpers.alert(e.message || 'Could not update rule')
  }
}

async function onSaved() {
  await loadRules()
  mode.value = 'browse'
  draft.value = null
}

async function onDeleted() {
  await loadRules()
  mode.value = 'browse'
  draft.value = null
}

function gliClick(about) {
  if (about && about.gli) proxy.global_lookup(about.gli, true)
}

onMounted(async () => {
  await Promise.all([loadCatalog(), loadRules()])
})
</script>

<template>
  <modal title="Notifications" size="xl" height="600px" :busy="loadingRules">
    <template #buttons>
      <b-form-select
        v-if="canAny"
        v-model="targetUser"
        :options="userOptions"
        size="sm"
        class="w-auto mr-2 border-0"
        style="max-width: 220px"
        @change="switchUser"/>
    </template>

    <div class="d-flex h-100 nr-root">
      <!-- LEFT: rule list -->
      <div class="nr-list border-right d-flex flex-column">
        <div class="p-2 border-bottom">
          <b-button variant="primary" size="sm" block @click="mode = 'browse'; draft = null">
            <font-awesome-icon :icon="['fas', 'plus']"/> New notification
          </b-button>
        </div>

        <div class="flex-fill overflow-auto nr-list-scroll">
          <b-alert :show="!!loadError" variant="danger" class="m-2">{{ loadError }}</b-alert>

          <div v-if="!rules.length && !loadingRules" class="text-muted text-center small p-4">
            No notifications yet.<br>Pick an event on the right to start.
          </div>

          <b-list-group flush>
            <b-list-group-item
              v-for="r in rules"
              :key="r.id"
              button
              class="nr-item py-2"
              :active="draft && draft.id === r.id"
              @click="editRule(r)">
              <div class="d-flex align-items-start">
                <font-awesome-icon
                  :icon="['fas', groupIcon(r.trigger_group)]"
                  class="mt-1 mr-2 text-muted"
                  fixed-width/>
                <div class="flex-fill min-w-0">
                  <div class="d-flex align-items-center">
                    <span class="font-weight-bold text-truncate">{{ r.name || r.trigger_label }}</span>
                    <font-awesome-icon
                      v-if="r.error"
                      :icon="['fas', 'triangle-exclamation']"
                      class="text-danger ml-1"
                      :title="r.error"/>
                  </div>
                  <div class="text-muted small text-truncate">{{ r.trigger_label }}</div>
                  <div class="mt-1">
                    <b-badge
                      v-if="r.about && r.about.gli"
                      variant="light"
                      class="border mr-1 cursor-pointer"
                      @click.stop="gliClick(r.about)">
                      <font-awesome-icon :icon="['fas', 'link']" size="xs"/> {{ r.about.gli }}
                    </b-badge>
                    <b-badge v-else-if="r.about" variant="light" class="border mr-1">
                      <font-awesome-icon :icon="['fas', 'filter']" size="xs"/> Any {{ r.about.type }}
                    </b-badge>
                    <font-awesome-icon v-if="r.focused_effective" :icon="['fas', 'bell']" size="xs"
                                       class="text-muted mr-1" title="Focused"/>
                    <b-badge v-if="r.fire_limit" variant="light" class="border text-muted"
                             title="Auto-deletes after this many deliveries">
                      <font-awesome-icon :icon="['far', 'clock']" size="xs"/>
                      {{ Math.max(0, r.fire_limit - r.match_count) }} left
                    </b-badge>
                    <b-badge v-else-if="r.fire_count" variant="light" class="border text-muted">
                      {{ r.fire_count }} sent
                    </b-badge>
                  </div>
                </div>
                <b-form-checkbox
                  switch
                  size="sm"
                  class="ml-1"
                  :checked="r.enabled"
                  @click.native.stop
                  @change="toggleEnabled(r)"/>
              </div>
            </b-list-group-item>
          </b-list-group>
        </div>
      </div>

      <!-- RIGHT: browse catalogue / edit / activity -->
      <div class="nr-detail flex-fill overflow-auto">
        <!-- BROWSE -->
        <div v-if="mode === 'browse'" class="p-3">
          <h6 class="text-muted text-uppercase small mb-3">Notify me when&hellip;</h6>
          <b-alert :show="!catalog && !loadError" variant="light">Loading events&hellip;</b-alert>

          <div v-for="(triggers, group) in displayGroups" :key="group" class="mb-3">
            <div class="d-flex align-items-center text-muted small font-weight-bold mb-1">
              <font-awesome-icon :icon="['fas', groupIcon(group)]" fixed-width class="mr-2"/>
              {{ group }}
            </div>
            <b-list-group>
              <b-list-group-item
                v-for="t in triggers"
                :key="t.key + (t.scope || '')"
                button
                class="d-flex align-items-center py-2"
                @click="startNew(t, t.scope)">
                <div class="flex-fill">
                  <div class="font-weight-bold">{{ t.label }}</div>
                  <div class="text-muted small" v-if="t.description">{{ t.description }}</div>
                </div>
                <font-awesome-icon :icon="['fas', 'chevron-right']" class="text-muted"/>
              </b-list-group-item>
            </b-list-group>
          </div>
        </div>

        <!-- EDIT -->
        <notification-rule-editor
          v-else-if="mode === 'edit' && draft"
          :key="draft.id || draft.action"
          :rule="draft"
          :base-path="basePath"
          :channels="(catalog && catalog.channels) || ['inapp', 'push']"
          :operators="(catalog && catalog.operators) || []"
          @cancel="mode = 'browse'; draft = null"
          @saved="onSaved"
          @deleted="onDeleted"
          @activity="openActivity"/>

        <!-- ACTIVITY -->
        <notification-rule-activity
          v-else-if="mode === 'activity' && activityRule"
          :rule="activityRule"
          @back="editRule(activityRule)"/>
      </div>
    </div>
  </modal>
</template>

<style scoped>
.nr-root {
  min-height: 0;
}

.nr-list {
  width: 300px;
  min-width: 300px;
  min-height: 0;
}

.nr-list-scroll {
  min-height: 0;
}

.nr-detail {
  min-height: 0;
}

.nr-item.active {
  background: rgba(0, 123, 255, .08);
}

.min-w-0 {
  min-width: 0;
}

.cursor-pointer {
  cursor: pointer;
}
</style>
